import React, { useState, useEffect } from 'react';
import { Button, Col, Row, Form, Card, Spinner, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Select from 'react-select';
import axios from '../../../api/axios';
import Header from '../../../layouts/Header';

const AddMenuIngredients = () => {
    const navigate = useNavigate();
    const { access } = useSelector((state) => state.login);
    const { email } = useSelector((state) => state.login);
    const accessToken = localStorage.getItem("authToken");

    const [categoryList, setCategoryList] = useState([]);
    const [filteredProductList, setFilteredProductList] = useState([]);
    const [rawMaterialList, setRawMaterialList] = useState([]);
    const [addons, setAddons] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [addCategoryId, setAddCategoryId] = useState('');

    const getDefaultConditionalAddon = () => ({
        addonId: '',
        addonValueId: '',
        quantity: ''
    });

    const getDefaultRawMaterial = () => ({
        rawMaterial: '',
        quantity: '',
        unit: '',
        addonId: '',
        addonValueId: '',
        isImportant: false,
        isOptional: false,
        conditionalAddons: []
    });

    const [inputData, setInputData] = useState({
        product: '',
        rawMaterials: [getDefaultRawMaterial()],
    });

    useEffect(() => {
        if (!access?.includes("MenuIngredients:add") && email !== "contact@xpansecafe.com") {
            toast.error("You don't have permission to access this page");
            navigate('/menu/menu-ingredients');
        }
    }, [access, email, navigate]);

    useEffect(() => {
        fetchInitialData();
        getProductsList();
    }, []);

    const fetchInitialData = async () => {
        setIsLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };

            const categoryRes = await axios.get("masters/category/get", { headers });
            if (categoryRes?.data?.status === true) {
                setCategoryList(categoryRes?.data?.data?.categories || []);
            }

            const rawMaterialRes = await axios.get("raw-material", { headers });
            if (rawMaterialRes?.data?.status === true) {
                setRawMaterialList(rawMaterialRes?.data?.data?.rawMaterials || []);
            }

            const addonsRes = await axios.get("addons?page=1&limit=1000", { headers });
            setAddons(addonsRes?.data?.data?.addons || []);
        } catch (error) {
            toast.error("Error fetching initial data");
        } finally {
            setIsLoading(false);
        }
    };

    const getProductsList = async (id) => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("product", { headers });
            const products = response?.data?.data?.products || [];

            if (id) {
                const filtered = products.filter((cat) => cat?.category?._id === id);
                setFilteredProductList(filtered);
            } else {
                setFilteredProductList(products);
            }
        } catch (error) {
            toast.error("Error fetching products");
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInputData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleRawMaterialChange = (index, field, value) => {
        const updatedRawMaterials = [...inputData.rawMaterials];
        updatedRawMaterials[index] = {
            ...updatedRawMaterials[index],
            [field]: value
        };

        if (field === 'rawMaterial' && value) {
            const selectedMaterial = rawMaterialList.find((rm) => rm._id === value);
            updatedRawMaterials[index].unit = selectedMaterial?.unit || "";
        }

        if (field === 'rawMaterial' && !value) {
            updatedRawMaterials[index].unit = "";
        }

        if (field === 'addonId') {
            updatedRawMaterials[index].addonValueId = "";
            updatedRawMaterials[index].conditionalAddons = [];
        }

        setInputData((prev) => ({
            ...prev,
            rawMaterials: updatedRawMaterials
        }));
    };

    const addRawMaterialEntry = () => {
        setInputData((prevData) => ({
            ...prevData,
            rawMaterials: [...prevData.rawMaterials, getDefaultRawMaterial()],
        }));
    };

    const removeRawMaterialEntry = (index) => {
        setInputData((prevData) => {
            const newRawMaterials = [...prevData.rawMaterials];
            newRawMaterials.splice(index, 1);

            if (newRawMaterials.length === 0) {
                newRawMaterials.push(getDefaultRawMaterial());
            }

            return {
                ...prevData,
                rawMaterials: newRawMaterials
            };
        });
    };

    const addConditionalAddon = (rawMaterialIndex) => {
        setInputData((prev) => {
            const updatedRawMaterials = [...prev.rawMaterials];
            updatedRawMaterials[rawMaterialIndex].conditionalAddons = [
                ...(updatedRawMaterials[rawMaterialIndex].conditionalAddons || []),
                getDefaultConditionalAddon()
            ];

            return {
                ...prev,
                rawMaterials: updatedRawMaterials
            };
        });
    };

    const removeConditionalAddon = (rawMaterialIndex, conditionalIndex) => {
        setInputData((prev) => {
            const updatedRawMaterials = [...prev.rawMaterials];
            const updatedConditionalAddons = [
                ...(updatedRawMaterials[rawMaterialIndex].conditionalAddons || [])
            ];

            updatedConditionalAddons.splice(conditionalIndex, 1);
            updatedRawMaterials[rawMaterialIndex].conditionalAddons = updatedConditionalAddons;

            return {
                ...prev,
                rawMaterials: updatedRawMaterials
            };
        });
    };

    const handleConditionalAddonChange = (rawMaterialIndex, conditionalIndex, field, value) => {
        setInputData((prev) => {
            const updatedRawMaterials = [...prev.rawMaterials];
            const updatedConditionalAddons = [
                ...(updatedRawMaterials[rawMaterialIndex].conditionalAddons || [])
            ];

            updatedConditionalAddons[conditionalIndex] = {
                ...updatedConditionalAddons[conditionalIndex],
                [field]: value
            };

            if (field === 'addonId') {
                updatedConditionalAddons[conditionalIndex].addonValueId = '';
            }

            updatedRawMaterials[rawMaterialIndex].conditionalAddons = updatedConditionalAddons;

            return {
                ...prev,
                rawMaterials: updatedRawMaterials
            };
        });
    };

    const validatePayload = () => {
        if (!inputData.product) {
            toast.error("Please select menu");
            return false;
        }

        for (let i = 0; i < inputData.rawMaterials.length; i++) {
            const rm = inputData.rawMaterials[i];

            if (!rm.rawMaterial) {
                toast.error(`Please select raw material for item #${i + 1}`);
                return false;
            }

            if (!rm.quantity) {
                toast.error(`Please enter quantity for raw material #${i + 1}`);
                return false;
            }

            if ((rm.addonId && !rm.addonValueId) || (!rm.addonId && rm.addonValueId)) {
                toast.error(`Please select both addon group and addon value for raw material #${i + 1}`);
                return false;
            }

            for (let j = 0; j < (rm.conditionalAddons || []).length; j++) {
                const cond = rm.conditionalAddons[j];
                if (!cond.addonId || !cond.addonValueId || !cond.quantity) {
                    toast.error(`Please complete conditional addon #${j + 1} for raw material #${i + 1}`);
                    return false;
                }
            }
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validatePayload()) return;

        setIsSubmitting(true);

        try {
            const headers = { Authorization: `Bearer ${accessToken}` };

            const payload = {
                product: inputData.product,
                rawMaterials: inputData.rawMaterials.map((rm) => ({
                    rawMaterial: rm.rawMaterial,
                    quantity: Number(rm.quantity),
                    isOptional: rm.isOptional || false,
                    addonId: rm.addonId || null,
                    addonValueId: rm.addonValueId || null,
                    isImportant: rm.isImportant || false,
                    conditionalAddons: (rm.conditionalAddons || []).map((cond) => ({
                        addonId: cond.addonId,
                        addonValueId: cond.addonValueId,
                        quantity: Number(cond.quantity)
                    }))
                }))
            };

            const response = await axios.post("recipe", payload, { headers });

            if (response?.data?.status === true) {
                toast.success("Ingredients added successfully");
                navigate('/menu/menu-ingredients');
            } else {
                toast.error(response?.data?.message || "Failed to add ingredients");
            }
        } catch (error) {
            let errorMessage = "Error adding ingredients";
            if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const categoryOptions = categoryList?.map((cat) => ({
        value: cat._id,
        label: cat.name,
    }));

    if (isLoading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center">
                    <Spinner animation="border" variant="primary" />
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div
                    className="container"
                    style={{ backgroundColor: "white", padding: "1.3rem", borderRadius: "8px" }}
                >
                    <div className="d-flex align-items-center mb-4">
                        <Button
                            variant="link"
                            className="text-decoration-none p-0 me-3"
                            onClick={() => navigate('/menu/menu-ingredients')}
                        >
                            <i className="ri-arrow-left-line fs-5"></i>
                        </Button>
                        <h5 className="fw-semibold mb-0">ADD MENU INGREDIENTS</h5>
                    </div>

                    <Form onSubmit={handleSubmit}>
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body>
                                <h6 className="fw-semibold mb-3">Product Information</h6>
                                <Row>
                                    <Col md="6">
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold small">Category</Form.Label>
                                            <Select
                                                options={categoryOptions}
                                                value={categoryOptions?.find((option) => option.value === addCategoryId) || null}
                                                onChange={(selectedOption) => {
                                                    const value = selectedOption?.value || '';
                                                    setAddCategoryId(value);
                                                    if (value) {
                                                        getProductsList(value);
                                                    } else {
                                                        getProductsList();
                                                    }
                                                }}
                                                placeholder="Select Category"
                                                isClearable
                                                classNamePrefix="react-select"
                                                className="small"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md="6">
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold small">
                                                Menu <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Select
                                                options={filteredProductList?.map((item) => ({
                                                    value: item?._id,
                                                    label: item?.name,
                                                }))}
                                                value={
                                                    inputData.product
                                                        ? {
                                                            value: inputData.product,
                                                            label:
                                                                filteredProductList?.find(
                                                                    (item) => item._id === inputData.product
                                                                )?.name || "Selected Menu",
                                                        }
                                                        : null
                                                }
                                                onChange={(selectedOption) => {
                                                    handleChange({
                                                        target: {
                                                            name: "product",
                                                            value: selectedOption ? selectedOption.value : "",
                                                        },
                                                    });
                                                }}
                                                classNamePrefix="react-select"
                                                isClearable
                                                placeholder="Select Menu"
                                                className="small"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <Card className="border-0 shadow-sm">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-semibold mb-0">Raw Materials</h6>
                                    <Badge bg="info" className="small">
                                        Required fields marked with *
                                    </Badge>
                                </div>

                                {inputData.rawMaterials.map((entry, index) => {
                                    const selectedAddon = addons?.find((a) => a?._id === entry.addonId);

                                    return (
                                        <Card key={index} className="mb-4 border">
                                            <Card.Body className="bg-light">
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <Badge bg="secondary">Material #{index + 1}</Badge>
                                                    {inputData.rawMaterials.length > 1 && (
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => removeRawMaterialEntry(index)}
                                                        >
                                                            <i className="ri-delete-bin-line me-1"></i>Remove
                                                        </Button>
                                                    )}
                                                </div>

                                                <Row className="mb-3">
                                                    <Col md={4}>
                                                        <Form.Label className="fw-semibold small">
                                                            Raw Material <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <Select
                                                            options={rawMaterialList.map((rm) => ({
                                                                value: rm._id,
                                                                label: `${rm.name} ${rm.inStock === false ? "(Out of stock)" : ""}`,
                                                            }))}
                                                            value={
                                                                entry.rawMaterial
                                                                    ? {
                                                                        value: entry.rawMaterial,
                                                                        label:
                                                                            rawMaterialList.find(
                                                                                (rm) => rm._id === entry.rawMaterial
                                                                            )?.name || "Selected Raw Material",
                                                                    }
                                                                    : null
                                                            }
                                                            onChange={(selectedOption) =>
                                                                handleRawMaterialChange(
                                                                    index,
                                                                    "rawMaterial",
                                                                    selectedOption ? selectedOption.value : ""
                                                                )
                                                            }
                                                            classNamePrefix="react-select"
                                                            isClearable
                                                            placeholder="Select Raw Material"
                                                            className="small"
                                                        />
                                                    </Col>

                                                    <Col md={3}>
                                                        <Form.Label className="fw-semibold small">
                                                            Quantity <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            size="sm"
                                                            placeholder="Enter quantity"
                                                            value={entry.quantity}
                                                            onChange={(e) =>
                                                                handleRawMaterialChange(index, "quantity", e.target.value)
                                                            }
                                                        />
                                                    </Col>

                                                    <Col md={2}>
                                                        <Form.Label className="fw-semibold small">Unit</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            size="sm"
                                                            disabled
                                                            placeholder="Unit"
                                                            value={entry.unit}
                                                        />
                                                    </Col>

                                                    <Col md={3} className="d-flex flex-column justify-content-center">
                                                        <Form.Check
                                                            type="checkbox"
                                                            label="Is Important"
                                                            checked={entry.isImportant || false}
                                                            onChange={(e) =>
                                                                handleRawMaterialChange(index, "isImportant", e.target.checked)
                                                            }
                                                            className="small mb-2"
                                                        />
                                                    </Col>
                                                </Row>

                                                <Row className="mb-3">
                                                    <Col md={6}>
                                                        <Form.Label className="fw-semibold small">Addon Group</Form.Label>
                                                        <Select
                                                            options={addons?.map((addon) => ({
                                                                value: addon?._id,
                                                                label: addon?.title,
                                                            }))}
                                                            value={
                                                                entry.addonId
                                                                    ? {
                                                                        value: entry.addonId,
                                                                        label:
                                                                            addons?.find((a) => a?._id === entry.addonId)
                                                                                ?.title || "Selected Addon Group",
                                                                    }
                                                                    : null
                                                            }
                                                            onChange={(selectedOption) => {
                                                                handleRawMaterialChange(
                                                                    index,
                                                                    "addonId",
                                                                    selectedOption?.value || ""
                                                                );
                                                            }}
                                                            classNamePrefix="react-select"
                                                            isClearable
                                                            placeholder="Select Addon Group (Optional)"
                                                            className="small"
                                                        />
                                                    </Col>

                                                    <Col md={6}>
                                                        <Form.Label className="fw-semibold small">Addon Value</Form.Label>
                                                        <Select
                                                            options={
                                                                selectedAddon?.addonValues?.map((val) => ({
                                                                    value: val._id,
                                                                    label: val.value,
                                                                })) || []
                                                            }
                                                            value={
                                                                entry.addonValueId && entry.addonId
                                                                    ? {
                                                                        value: entry.addonValueId,
                                                                        label:
                                                                            selectedAddon?.addonValues?.find(
                                                                                (v) => v._id === entry.addonValueId
                                                                            )?.value || "Selected Addon Value",
                                                                    }
                                                                    : null
                                                            }
                                                            onChange={(selectedOption) =>
                                                                handleRawMaterialChange(
                                                                    index,
                                                                    "addonValueId",
                                                                    selectedOption?.value || ""
                                                                )
                                                            }
                                                            classNamePrefix="react-select"
                                                            isClearable
                                                            placeholder={
                                                                entry.addonId
                                                                    ? "Select Addon Value"
                                                                    : "Select Addon Group first"
                                                            }
                                                            isDisabled={!entry.addonId}
                                                            className="small"
                                                        />
                                                    </Col>
                                                </Row>

                                                {entry.addonId && entry.addonValueId && (
                                                    <Card className="border mt-3">
                                                        <Card.Body className="bg-white">
                                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                                <div>
                                                                    <h6 className="mb-0">Conditional Addons</h6>
                                                                    <small className="text-muted">
                                                                        Add conditions for this raw material based on addon selection
                                                                    </small>
                                                                </div>
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={() => addConditionalAddon(index)}
                                                                >
                                                                    <i className="ri-add-line me-1"></i>Add Condition
                                                                </Button>
                                                            </div>

                                                            {(entry.conditionalAddons || []).length === 0 && (
                                                                <div className="text-muted small">
                                                                    No conditional addons added yet.
                                                                </div>
                                                            )}

                                                            {(entry.conditionalAddons || []).map((cond, condIndex) => {
                                                                const condAddon = addons?.find((a) => a?._id === cond.addonId);

                                                                return (
                                                                    <Card key={condIndex} className="mb-3 border">
                                                                        <Card.Body className="bg-light">
                                                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                                                <Badge bg="dark">
                                                                                    Condition #{condIndex + 1}
                                                                                </Badge>
                                                                                <Button
                                                                                    variant="outline-danger"
                                                                                    size="sm"
                                                                                    onClick={() =>
                                                                                        removeConditionalAddon(index, condIndex)
                                                                                    }
                                                                                >
                                                                                    Remove
                                                                                </Button>
                                                                            </div>

                                                                            <Row>
                                                                                <Col md={4}>
                                                                                    <Form.Label className="fw-semibold small">
                                                                                        Addon Group <span className="text-danger">*</span>
                                                                                    </Form.Label>
                                                                                    <Select
                                                                                        options={addons?.map((addon) => ({
                                                                                            value: addon?._id,
                                                                                            label: addon?.title,
                                                                                        }))}
                                                                                        value={
                                                                                            cond.addonId
                                                                                                ? {
                                                                                                    value: cond.addonId,
                                                                                                    label:
                                                                                                        addons?.find(
                                                                                                            (a) => a?._id === cond.addonId
                                                                                                        )?.title ||
                                                                                                        "Selected Addon Group",
                                                                                                }
                                                                                                : null
                                                                                        }
                                                                                        onChange={(selectedOption) =>
                                                                                            handleConditionalAddonChange(
                                                                                                index,
                                                                                                condIndex,
                                                                                                "addonId",
                                                                                                selectedOption?.value || ""
                                                                                            )
                                                                                        }
                                                                                        classNamePrefix="react-select"
                                                                                        isClearable
                                                                                        placeholder="Select Addon Group"
                                                                                        className="small"
                                                                                    />
                                                                                </Col>

                                                                                <Col md={4}>
                                                                                    <Form.Label className="fw-semibold small">
                                                                                        Addon Value <span className="text-danger">*</span>
                                                                                    </Form.Label>
                                                                                    <Select
                                                                                        options={
                                                                                            condAddon?.addonValues?.map((val) => ({
                                                                                                value: val._id,
                                                                                                label: val.value,
                                                                                            })) || []
                                                                                        }
                                                                                        value={
                                                                                            cond.addonValueId && cond.addonId
                                                                                                ? {
                                                                                                    value: cond.addonValueId,
                                                                                                    label:
                                                                                                        condAddon?.addonValues?.find(
                                                                                                            (v) =>
                                                                                                                v._id === cond.addonValueId
                                                                                                        )?.value ||
                                                                                                        "Selected Addon Value",
                                                                                                }
                                                                                                : null
                                                                                        }
                                                                                        onChange={(selectedOption) =>
                                                                                            handleConditionalAddonChange(
                                                                                                index,
                                                                                                condIndex,
                                                                                                "addonValueId",
                                                                                                selectedOption?.value || ""
                                                                                            )
                                                                                        }
                                                                                        classNamePrefix="react-select"
                                                                                        isClearable
                                                                                        placeholder={
                                                                                            cond.addonId
                                                                                                ? "Select Addon Value"
                                                                                                : "Select Addon Group first"
                                                                                        }
                                                                                        isDisabled={!cond.addonId}
                                                                                        className="small"
                                                                                    />
                                                                                </Col>

                                                                                <Col md={4}>
                                                                                    <Form.Label className="fw-semibold small">
                                                                                        Quantity <span className="text-danger">*</span>
                                                                                    </Form.Label>
                                                                                    <div className="input-group input-group-sm">
                                                                                        <Form.Control
                                                                                            type="number"
                                                                                            placeholder="Enter quantity"
                                                                                            value={cond.quantity}
                                                                                            onChange={(e) =>
                                                                                                handleConditionalAddonChange(
                                                                                                    index,
                                                                                                    condIndex,
                                                                                                    "quantity",
                                                                                                    e.target.value
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                        <span className="input-group-text">
                                                                                            {entry.unit || "Unit"}
                                                                                        </span>
                                                                                    </div>
                                                                                </Col>
                                                                            </Row>
                                                                        </Card.Body>
                                                                    </Card>
                                                                );
                                                            })}
                                                        </Card.Body>
                                                    </Card>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    );
                                })}

                                <Button
                                    variant="outline-success"
                                    size="sm"
                                    className="mt-2"
                                    onClick={addRawMaterialEntry}
                                >
                                    <i className="ri-add-line me-1"></i> Add Another Raw Material
                                </Button>
                            </Card.Body>
                        </Card>

                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => navigate('/menu/menu-ingredients')}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                size="sm"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Ingredients'
                                )}
                            </Button>
                        </div>
                    </Form>
                </div>
            </div>
        </>
    );
};

export default AddMenuIngredients;